import React, { useEffect, useState } from 'react';
import { useVehicle } from '../../contexts/VehicleContext';
import { FaSearch, FaCar, FaIdCard, FaPlus } from 'react-icons/fa';

const VehicleLookup = ({ onVehicleSelected, onCancel, embedded = false, showTitle = true }) => {
  const {
    lookupByVIN,
    lookupByRegistration,
    searchByFilters,
    addVehicle,
    fetchMakes,
    fetchModels
  } = useVehicle();
  const [searchType, setSearchType] = useState('registration'); // 'registration' or 'vin' or 'filters' or 'add'
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState({
    make: '',
    model: '',
    variant: '',
    year: '',
    fuelType: ''
  });
  const [manualVehicle, setManualVehicle] = useState({
    make: '',
    model: '',
    variant: '',
    year: '',
    fuelType: '',
    registrationNumber: '',
    vin: ''
  });
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [makeOptions, setMakeOptions] = useState([]);
  const [manualModelOptions, setManualModelOptions] = useState([]);
  const [filterModelOptions, setFilterModelOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const makes = await fetchMakes();
      if (!cancelled) setMakeOptions(Array.isArray(makes) ? makes : []);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchMakes]);

  useEffect(() => {
    if (!manualVehicle.make) {
      setManualModelOptions([]);
      setManualVehicle((prev) => ({ ...prev, model: '' }));
      return;
    }
    let cancelled = false;
    (async () => {
      const models = await fetchModels(manualVehicle.make);
      if (!cancelled) {
        setManualModelOptions(Array.isArray(models) ? models : []);
        setManualVehicle((prev) => ({ ...prev, model: '' }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [manualVehicle.make, fetchModels]);

  useEffect(() => {
    if (!filters.make) {
      setFilterModelOptions([]);
      setFilters((prev) => ({ ...prev, model: '' }));
      return;
    }
    let cancelled = false;
    (async () => {
      const models = await fetchModels(filters.make);
      if (!cancelled) {
        setFilterModelOptions(Array.isArray(models) ? models : []);
        setFilters((prev) => ({ ...prev, model: '' }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filters.make, fetchModels]);

  const handleSearch = async () => {
    setError('');
    setResults([]);

    if (searchType === 'registration') {
      try {
        const vehicle = await lookupByRegistration(searchValue);
        if (vehicle) {
          let saved = vehicle;
          if (!vehicle._id && !vehicle.id && addVehicle) {
            saved = await addVehicle({
              make: vehicle.make || '',
              model: vehicle.model || '',
              variant: vehicle.variant || '',
              year: vehicle.year || '',
              fuelType: vehicle.fuelType || '',
              registrationNumber: vehicle.registrationNumber || searchValue.trim(),
              regNo: vehicle.registrationNumber || searchValue.trim(),
              vin: vehicle.vin || '',
            });
          }
          const result = saved || vehicle;
          setResults([result]);
          onVehicleSelected?.(result);
        } else {
          setError('Vehicle not found. Please check your registration number.');
        }
      } catch (lookupErr) {
        setError(lookupErr.message || 'Registration lookup failed. Try Add New Vehicle instead.');
      }
    } else if (searchType === 'vin') {
      const vehicle = lookupByVIN(searchValue);
      if (vehicle) {
        setResults([vehicle]);
        onVehicleSelected?.(vehicle);
      } else {
        setError('Vehicle not found. Please check your VIN.');
      }
    } else if (searchType === 'filters') {
      const found = searchByFilters(filters);
      setResults(found);
      if (found.length === 0 && (filters.make || filters.model)) {
        setError('No vehicles found. Add this as a new vehicle using the button below.');
      } else if (found.length === 0) {
        setError('Enter make and model to add a new vehicle.');
      }
    }
  };

  const tabClass = (active) =>
    `px-3 py-2 text-sm font-semibold transition-colors sm:px-4 sm:text-base ${
      active
        ? 'text-red-600 border-b-2 border-red-600'
        : 'text-gray-600 hover:text-red-600'
    }`;

  const backBtnClass =
    'px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium';

  const shellClass = embedded ? 'w-full' : 'card';

  return (
    <div className={shellClass}>
      {showTitle && (
        <h2 className="mb-4 text-xl font-bold text-gray-900 sm:mb-6 sm:text-2xl">Find Your Vehicle</h2>
      )}

      {/* Search Type Tabs */}
      <div className="mb-6 flex gap-1 overflow-x-auto border-b border-gray-200 pb-0 scrollbar-hide sm:flex-wrap sm:gap-2">
        <button
          onClick={() => { setSearchType('registration'); setError(''); }}
          className={tabClass(searchType === 'registration')}
        >
          <FaIdCard className="inline mr-2" />
          Registration Number
        </button>
        <button
          onClick={() => { setSearchType('vin'); setError(''); }}
          className={`shrink-0 ${tabClass(searchType === 'vin')}`}
        >
          <FaCar className="inline mr-2" />
          VIN
        </button>
        <button
          onClick={() => { setSearchType('filters'); setError(''); }}
          className={`shrink-0 ${tabClass(searchType === 'filters')}`}
        >
          <FaSearch className="inline mr-2" />
          Filters
        </button>
        <button
          onClick={() => { setSearchType('add'); setError(''); }}
          className={`shrink-0 ${tabClass(searchType === 'add')}`}
        >
          <FaPlus className="inline mr-2" />
          Add New Vehicle
        </button>
      </div>

      {/* Add New Vehicle Form */}
      {searchType === 'add' ? (
        <div className="space-y-4 mb-6">
          <p className="text-sm text-gray-600">Enter your vehicle details to add it to your account.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Make *</label>
              <select
                value={manualVehicle.make}
                onChange={(e) => setManualVehicle({ ...manualVehicle, make: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">Select Make</option>
                {makeOptions.map((make) => (
                  <option key={make.makerId} value={make.value}>
                    {make.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Model *</label>
              <select
                value={manualVehicle.model}
                onChange={(e) => setManualVehicle({ ...manualVehicle, model: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                disabled={!manualVehicle.make}
              >
                <option value="">Select Model</option>
                {manualModelOptions.map((model) => (
                  <option key={model.modelId} value={model.value}>
                    {model.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Variant</label>
              <input
                type="text"
                value={manualVehicle.variant}
                onChange={(e) => setManualVehicle({ ...manualVehicle, variant: e.target.value })}
                placeholder="e.g., VDI"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <input
                type="number"
                value={manualVehicle.year}
                onChange={(e) => setManualVehicle({ ...manualVehicle, year: e.target.value })}
                placeholder="e.g., 2020"
                min="1990"
                max={new Date().getFullYear() + 1}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fuel Type</label>
              <select
                value={manualVehicle.fuelType}
                onChange={(e) => setManualVehicle({ ...manualVehicle, fuelType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">Select</option>
                <option value="Petrol">Petrol</option>
                <option value="Diesel">Diesel</option>
                <option value="CNG">CNG</option>
                <option value="Electric">Electric</option>
                <option value="Hybrid">Hybrid</option>
              </select>
            </div>
            {/* Registration number is intentionally not collected from user in this flow */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">VIN (optional)</label>
              <input
                type="text"
                value={manualVehicle.vin}
                onChange={(e) => setManualVehicle({ ...manualVehicle, vin: e.target.value.toUpperCase() })}
                placeholder="17-digit VIN"
                maxLength={17}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
          </div>
          <div className="flex gap-3">
            {onCancel && (
              <button type="button" onClick={onCancel} className={backBtnClass}>
                Back
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (!manualVehicle.make?.trim() || !manualVehicle.model?.trim()) {
                  setError('Make and Model are required.');
                  return;
                }
                setError('');
                const newV = addVehicle({
                  make: manualVehicle.make.trim(),
                  model: manualVehicle.model.trim(),
                  variant: manualVehicle.variant?.trim() || '',
                  year: manualVehicle.year?.toString() || '',
                  fuelType: manualVehicle.fuelType || '',
                  // Do not allow user-entered registration number here
                  registrationNumber: '',
                  regNo: '',
                  vin: manualVehicle.vin?.trim() || ''
                });
                onVehicleSelected?.(newV);
              }}
              className="flex-1 py-3 px-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 flex items-center justify-center gap-2"
            >
              <FaPlus />
              Add Vehicle
            </button>
          </div>
        </div>
      ) : searchType === 'filters' ? (
        <div className="space-y-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Make</label>
              <select
                value={filters.make}
                onChange={(e) => setFilters({ ...filters, make: e.target.value })}
                className="input-field"
              >
                <option value="">Select Make</option>
                {makeOptions.map((make) => (
                  <option key={make.makerId} value={make.value}>
                    {make.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
              <select
                value={filters.model}
                onChange={(e) => setFilters({ ...filters, model: e.target.value })}
                className="input-field"
                disabled={!filters.make}
              >
                <option value="">Select Model</option>
                {filterModelOptions.map((model) => (
                  <option key={model.modelId} value={model.value}>
                    {model.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Variant</label>
              <input
                type="text"
                value={filters.variant}
                onChange={(e) => setFilters({ ...filters, variant: e.target.value })}
                placeholder="e.g., VDI"
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <input
                type="number"
                value={filters.year}
                onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                placeholder="e.g., 2020"
                className="input-field"
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-6">
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {searchType === 'registration' ? 'Registration Number' : 'VIN'}
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value.toUpperCase())}
              placeholder={searchType === 'registration' ? 'MH12AB1234' : 'Enter 17-digit VIN'}
              className="input-field flex-1"
              maxLength={searchType === 'vin' ? 17 : undefined}
            />
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
          {error}
          {searchType === 'filters' && filters.make && filters.model && (
            <button
              type="button"
              onClick={() => {
                const newV = addVehicle({
                  make: filters.make,
                  model: filters.model,
                  variant: filters.variant,
                  year: filters.year,
                  fuelType: filters.fuelType,
                  registrationNumber: '',
                  vin: ''
                });
                onVehicleSelected?.(newV);
              }}
              className="block mt-2 text-amber-700 font-semibold hover:underline"
            >
              + Add as new vehicle
            </button>
          )}
        </div>
      )}

      {searchType !== 'add' && (
        <div className="mb-6 flex flex-col gap-3 sm:flex-row">
          <button onClick={handleSearch} className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 font-semibold text-white hover:bg-red-700">
            <FaSearch className="inline" />
            Search Vehicle
          </button>
          {onCancel && (
            <button type="button" onClick={onCancel} className={`sm:shrink-0 ${backBtnClass}`}>
              Back
            </button>
          )}
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-900">Search Results:</h3>
          {results.map((vehicle) => (
            <div
              key={vehicle._id || vehicle.id || `${vehicle.make}-${vehicle.model}-${vehicle.registrationNumber}`}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition cursor-pointer"
              onClick={() => onVehicleSelected?.(vehicle)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-gray-900">
                    {vehicle.make} {vehicle.model} {vehicle.variant}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {vehicle.year} • {vehicle.fuelType} • {vehicle.engine}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Reg: {vehicle.registrationNumber} | VIN: {vehicle.vin}
                  </p>
                </div>
                <button className="btn-primary text-sm">
                  Select
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VehicleLookup;

